import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  CHANNEL_HIGHLIGHT_INSTAGRAM_LOGIN,
  CHANNEL_HIGHLIGHT_PARAM,
} from '~publish/helpers/channelHighlight'
import { isInstagramLoginChannel } from '~publish/helpers/channels'

import type { LinkFacebookPageHint } from './LinkFacebookPageCoachmark'

type HintableChannel = {
  id: string
  service?: string | null
  apiVersion?: string | null
}

type LinkFacebookPageHints = {
  /** Hint per channel id; absent for channels that need no linking. */
  hints: Map<string, LinkFacebookPageHint>
  /** Clears the highlight, retiring every coach mark on the page. */
  dismiss: () => void
}

/**
 * Decides which channel rows point at the Facebook-Page link. Every affected
 * channel gets a spotlight; only the first also gets the explanation card.
 *
 * Deliberately not persisted: someone who links two of four accounts and comes
 * back through the notice should be coached again.
 */
export const useLinkFacebookPageHints = (
  channels: readonly HintableChannel[],
): LinkFacebookPageHints => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isHighlighted =
    searchParams.get(CHANNEL_HIGHLIGHT_PARAM) ===
    CHANNEL_HIGHLIGHT_INSTAGRAM_LOGIN

  const hints = useMemo(() => {
    const result = new Map<string, LinkFacebookPageHint>()
    if (!isHighlighted) return result

    channels.filter(isInstagramLoginChannel).forEach((channel, index) => {
      result.set(channel.id, index === 0 ? 'card' : 'spotlight')
    })

    return result
  }, [channels, isHighlighted])

  const dismiss = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete(CHANNEL_HIGHLIGHT_PARAM)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  return { hints, dismiss }
}
